import * as yup from 'yup'

import {
  ALLOWED_CHARS,
  JUMP_URL_DCL,
  JUMP_URL_ROBLOX,
  JUMP_URL_VOXELS,
  JUMP_URL_VOXELS2,
  URL_CHARS,
  UUID_STRING,
} from 'constants/fieldsValidationValues'

const ACCEPTED_FILE_TYPES = ['image/jpg', 'image/png', 'image/jpeg']

export const makeAdSpaceSchema = (metaverse: string, id: number) =>
  yup.object().shape({
    name: yup
      .string()
      .trim()
      .required('Name is a required field')
      .matches(ALLOWED_CHARS, `Name can only contain latin letters`)
      .min(3, 'Name should contain atleast 3 symbols')
      .max(60, 'Name should contain up to 60 symbols'),
    size: yup.string(),
    metaverseType: yup.string(),
    externalLink: yup
      .string()
      .test('link-validation', `Invalid ${metaverse === 'Roblox' ? 'teleport ID' : 'link'}`, (value) => {
        if (!value?.length || id) return true // is optional
        if (metaverse === 'Roblox') return /\d+/.test(value)
        return URL_CHARS.test(value)
      })
      .max(255, 'Link should contain up to 255 symbols')
      .nullable(),
    description: yup
      .string()
      .trim()
      .required('Description is a required field')
      .matches(ALLOWED_CHARS, `Description can only contain latin letters`)
      .min(3, 'Description should contain atleast 3 symbols')
      .max(255, 'Description should contain up to 255 symbols'),
    files: yup
      .mixed()
      .test('fileRequired', 'Preview file is required', (value) => {
        if (id) return true
        return !!value?.length
      })
      .test('fileSize', 'Maximum image size is 2MB', (value) => {
        if (!value?.length) return true // attachment is optional
        return value[0]?.size <= 2 * 2 ** 20
      })
      .test('fileSizeMin', 'Minimum image size is 1 byte', (value) => {
        if (!value?.length) return true // attachment is optional
        return value[0].size > 0
      })
      .test('fileType', 'Preview should be of .jpg, .jpeg or .png type', (value) => {
        if (!value?.length) return true
        return ACCEPTED_FILE_TYPES.includes(value[0].type)
      }),
    jumpUrl: yup
      .string()
      .test('link-validation', 'Invalid link', (value) => {
        if (!value?.length || id) return true // is optional (id means that this is edit form)
        if (metaverse === 'Decentraland') return JUMP_URL_DCL.test(value)
        if (metaverse === 'Roblox') return JUMP_URL_ROBLOX.test(value)
        if (metaverse === 'Voxels') return JUMP_URL_VOXELS.test(value) || JUMP_URL_VOXELS2.test(value)
        return URL_CHARS.test(value)
      })
      .max(255, 'Link should contain up to 255 symbols')
      .nullable(),
    defaultFiles: yup
      .mixed()
      .test('fileSize', 'Maximum image size is 2MB', (value) => {
        if (!value?.length) return true // attachment is optional
        return value[0]?.size <= 2 * 2 ** 20
      })
      .test('fileSizeMin', 'Minimum image size is 1 byte', (value) => {
        if (!value?.length) return true // attachment is optional
        return value[0].size > 0
      })
      .test('fileType', 'Default should be of .jpg, .jpeg or .png type', (value) => {
        if (!value?.length) return true
        return ACCEPTED_FILE_TYPES.includes(value[0].type)
      }),
    robloxImgUrl: yup.string().test('link-validation', 'Invalid link', (value) => {
      if (!value?.length || id) return true // is optional
      return (
        RegExp('^http:\\/\\/www\\.roblox\\.com\\/asset\\/\\?id=[0-9]+').test(value) ||
        RegExp('^rbxassetid:\\/\\/[0-9]+').test(value || '')
      )
    }),
    unityUUID: yup.string().test('unity-uuid-validation', 'Invalid UUID', (value) => {
      if (metaverse !== 'Unity') return true
      return !!value?.length && UUID_STRING.test(value)
    }),
  })
